import React, { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { useDensity } from "../../theme/ThemeContext";
import type { Density } from "../../theme/types";

const textarea = cva(
  [
    "w-full rounded-sm border bg-surface-raised text-sm text-text outline-none",
    "placeholder:text-text-subtle",
    "transition-shadow focus:ring-2",
    "disabled:cursor-not-allowed disabled:bg-surface disabled:text-text-subtle",
  ],
  {
    variants: {
      state: {
        default: "border-border focus:ring-accent-600/30",
        error: "border-error-border focus:ring-error-border/60",
      },
      density: {
        comfortable: "px-3 py-2.5",
        compact: "px-2.5 py-1.5 text-xs",
      },
    },
    defaultVariants: { state: "default", density: "comfortable" },
  }
);

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  Omit<VariantProps<typeof textarea>, "density"> & { density?: Density };

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ state, density, className = "", rows = 4, ...props }, ref) => {
    const resolvedDensity = useDensity(density);
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(textarea({ state, density: resolvedDensity }), className)}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";
